import React, { useCallback, useMemo, useState } from 'react';
import { Col, Row, Input, Progress, Form, Typography } from 'antd';
import { SearchUrlType } from '@copilot/common/utils/constant';
import { SearchUrlValidator } from '@copilot/common/components/forms/common/validators';
import { Config } from '@copilot/common/config';
import { UtilityFunctions } from '@copilot/common/utils/common';
import MultipleInviteToggle from '../multipleInvite';
import { MultipleInvite } from '@copilot/data/responses/interface';
import Title from '@copilot/common/typography/title';
import { isSearchUrlValid } from '@copilot/common/utils';
import styles from './update.module.less';

const { Link, Paragraph } = Typography;

type SearchUrlUpdateModuleProps = {
	searchType: SearchUrlType;
	isInitialSearch: boolean;
	searchPercentage?: number;
	searchingText?: string;
	showVideoLink: boolean;
	saveAndSearchButton: (searchUrl: string) => JSX.Element;
	onVideoLinkClicked: () => void;
	saveDisableWarning?: React.ReactNode;
	multipleInvite?: MultipleInvite;
	updateMultipleInvite?: (multipleInvite: MultipleInvite) => void;
};

/**
 * Update section for the search url module
 * @param {SearchUrlType} searchType search type of the search url module
 * @param {boolean} isInitialSearch whether the current search is the initial search for the user
 * @param {number} searchPercentage flag to show the saving state of the save button and prevent further clicks
 * @param {boolean} showVideoLink whether to show the video link
 * @param {function} saveAndSearchButton button that gets clicked for save and search
 * @param {function} onVideoLinkClicked handles video link clicks
 * @param {ReactNode} saveDisableWarning warning to be displayed when a user isn't be able to save & search
 * @param {MultipleInvite} multipleInvite multiple invite value, prop for optional multiple invite toggle
 * @param {function} updateMultipleInvite handles multiple invite changes, prop for optional multiple invite toggle
 */
function SearchUrlUpdateModule({
	searchType,
	isInitialSearch,
	searchPercentage,
	showVideoLink,
	saveAndSearchButton,
	onVideoLinkClicked,
	saveDisableWarning,
	multipleInvite,
	updateMultipleInvite,
	searchingText = 'Search Running...',
}: SearchUrlUpdateModuleProps) {
	const isSearching = searchPercentage !== undefined;
	const [searchUrl, setSearchUrl] = useState<string>('');
	const handleSearchUrlChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchUrl(e.target.value);
	}, []);

	const searchUrlUpdateTitle = useMemo(() => {
		switch (searchType) {
			case SearchUrlType.Search:
				return 'Enter search URL:';
			case SearchUrlType.Nurture:
				return 'Add more connections from a Search URL';
			default:
				return UtilityFunctions.assertUnreachable(searchType);
		}
	}, [searchType]);

	const descriptionText = useMemo(() => {
		switch (searchType) {
			case SearchUrlType.Search:
				if (isSearching) {
					return (
						<Paragraph>
							We are attempting to bring in more prospects based on your URL. You may
							need to refresh to see more accurate status.
						</Paragraph>
					);
				} else if (Config.isAgency) {
					return <></>;
				} else return null;
			case SearchUrlType.Nurture:
				return (
					<>
						<Paragraph>
							{!isInitialSearch &&
								'Input a Sales Navigator URL above to import connections to this Nurture List. '}
							Please note, prospects are now going to be removed from their current
							campaign and sent to the selected campaign. All previous automations and
							scheduled message will <strong>NOT</strong> occur.
						</Paragraph>
						{showVideoLink && (
							<Paragraph>
								Be sure to follow our{' '}
								<Link onClick={onVideoLinkClicked}>BEST PRACTICES</Link> for
								creating the search URL.
							</Paragraph>
						)}
					</>
				);
			default:
				return UtilityFunctions.assertUnreachable(searchType);
		}
	}, [onVideoLinkClicked, searchType, isSearching, isInitialSearch]);

	return (
		<>
			<Row gutter={12} align={'middle'}>
				{isSearching ? (
					<Col span={12}>
						<Progress
							percent={searchPercentage}
							size={['100%', 30]}
							status="active"
							format={() => searchingText}
						/>
					</Col>
				) : (
					<>
						<Col>
							<Title level={5}>{searchUrlUpdateTitle}</Title>
						</Col>
						<Col span={24} lg={{ span: 18 }}>
							<Row gutter={[8, 4]}>
								<Col flex="auto" lg={{ span: 12 }}>
									<Form>
										<Form.Item
											name="searchUrl"
											className={styles.searchBarWrapper}
											rules={[
												{
													validator: SearchUrlValidator,
												},
											]}
										>
											<Input
												type="text"
												value={searchUrl}
												onChange={handleSearchUrlChange}
											/>
										</Form.Item>
									</Form>
								</Col>
								<Col>
									<Row align="middle" gutter={12}>
										<Col>{saveAndSearchButton(searchUrl)}</Col>
										<Col>
											{Boolean(saveDisableWarning) && saveDisableWarning}
										</Col>
									</Row>
								</Col>
							</Row>
							{!isSearching && multipleInvite && updateMultipleInvite && (
								<div className={styles.toggleWrapper}>
									<MultipleInviteToggle
										toggleLabel="Duplicate prospects"
										tooltipLabel="Include prospects who've been collected by other team members"
										multipleInvite={multipleInvite}
										disabled={!isSearchUrlValid(searchUrl)}
										onUpdate={updateMultipleInvite}
									/>
								</div>
							)}
						</Col>
					</>
				)}
			</Row>

			<div className={styles.descriptionWrapper}>{descriptionText}</div>
		</>
	);
}
export default SearchUrlUpdateModule;
